import React from "react";
import { useProduct } from "@/hooks/useProduct";
import type { ProductDetailBlock } from "@/types/product";
import { RichTextWithHeadingSection } from "./blocks/RichTextWithHeadingSection";
import { KeyBenefitsSection } from "./blocks/KeyBenefitsSection";
import { FeatureDeepDiveSection } from "./blocks/FeatureDeepDiveSection";
import { IncludedProductsSection } from "./blocks/IncludedProductsSection";
import { SpecificationsSection } from "./blocks/SpecificationsSection";
import { CrossPromoSection } from "./blocks/CrossPromoSection";
import { UpsellProductsSection } from "./blocks/UpsellProductsSection";
import { FaqSection } from "./blocks/FaqSection";

function renderBlock(block: ProductDetailBlock) {
  switch (block.type) {
    case "richTextWithHeading":
      return <RichTextWithHeadingSection key={block.id} block={block} />;
    case "specificationBlock":
      return <SpecificationsSection key={block.id} block={block} />;
    case "keyBenefitsBlock":
      return <KeyBenefitsSection key={block.id} block={block} />;
    case "featureDeepDiveBlock":
      return <FeatureDeepDiveSection key={block.id} block={block} />;
    case "includedProductsBlock":
      return <IncludedProductsSection key={block.id} block={block} />;
    case "crossPromoBlock":
      return <CrossPromoSection key={block.id} block={block} />;
    case "upsellProductsBlock":
      return <UpsellProductsSection key={block.id} block={block} />;
    case "faqBlock":
      return <FaqSection key={block.id} block={block} />;
    case "textAndLinkableImagesBlock":
      // No design section maps to this variant yet — skip silently.
      return null;
    default:
      if (import.meta.env.DEV) {
        const unknown = block as { type?: string; id?: string };
        console.warn(
          `[ProductDetailContent] Unknown block type "${unknown.type}" (id: ${unknown.id}) — no renderer registered.`
        );
      }
      return null;
  }
}

export const ProductDetailContent: React.FC = () => {
  const { productData } = useProduct();
  const blocks =
    productData.compositions?.productContent?.productDetailBlocks ?? [];

  if (!blocks.length) return null;

  return <>{blocks.map((block) => renderBlock(block))}</>;
};

export default ProductDetailContent;
